/* ============================================================
   widget.js — storefront chat widget for Shopify.

   Expects window.CHAT_CONFIG = { SUPABASE_URL, SUPABASE_ANON_KEY }
   to already be defined somewhere on the page before this file
   loads (see widget/snippet.liquid and config.example.js).

   Self-contained: loads supabase-js from a CDN itself, and loads
   widget.css itself (from the path next to this script).

   All reads/writes go through Postgres RPC functions (see
   supabase/schema.sql) instead of querying tables directly. Each
   function requires both conversationId and visitorId — two values
   that only ever exist together in this browser's localStorage —
   so the anon key alone isn't enough to read someone else's chat.
   There's no Realtime subscription for the same reason (Realtime
   can't be scoped per-visitor without real auth); instead, the
   widget polls for new messages continuously once a conversation
   exists (not just while the window is open), so it can show an
   unread badge + play a sound for new replies even while closed.

   Photo attachments upload straight to the public "chat-attachments"
   Storage bucket (anon can only INSERT there, not list — see
   supabase/schema.sql); the returned URL is stored on the message
   via the same RPC used for text. Photos are compressed client-side
   (Canvas) to roughly TARGET_IMAGE_BYTES before uploading, to keep
   Storage usage and load times down.

   Swipe a message right to reply to it (Telegram-style) — shows a
   quoted preview above the input while composing, and a small quote
   block inside the sent message once it lands.
   ============================================================ */

(function () {
  'use strict';

  var POLL_INTERVAL_MS = 4000;
  var MAX_INPUT_LINES = 5;
  var MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  var ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'];
  // These get converted to JPEG client-side before upload regardless of
  // size (see compressImage) — HEIC/WebP aren't reliably viewable
  // everywhere (e.g. HEIC needs Apple's own codec), so the file that
  // actually lands in Storage and the dashboard should always be plain
  // JPEG, not whatever the customer's camera happened to save.
  var TYPES_REQUIRING_CONVERSION = ['image/heic', 'image/heif', 'image/webp'];
  var TARGET_IMAGE_BYTES = 300 * 1024;
  var MAX_IMAGE_DIMENSION = 1600;
  var EMOJIS = ['😀', '😂', '😊', '😍', '😘', '👍', '👎', '🙏', '🎉', '❤️', '🔥', '😢', '😮', '😡', '🤔', '👋', '✅', '❌', '⭐', '💰', '📦', '🛍️', '😉', '🥳'];

  // The attach/emoji buttons show a plain-text emoji glyph on mobile
  // (simplest, always legible) and an SVG line icon on desktop (see
  // .sc-icon-emoji/.sc-icon-svg in widget.css) — both markups are
  // always in the DOM, CSS just toggles which one is visible per
  // viewport width.
  var ATTACH_ICON_SVG = '<svg class="sc-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 12V6a4 4 0 0 1 8 0v10a2 2 0 0 1-4 0V8"/></svg>';
  var EMOJI_ICON_SVG = '<svg class="sc-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><circle cx="9" cy="10" r="1" fill="currentColor" stroke="none"/><circle cx="15" cy="10" r="1" fill="currentColor" stroke="none"/><path d="M8 14.5c1 1.5 3 2 4 2s3-.5 4-2"/></svg>';

  // Bump this whenever widget.css changes. Shopify auto-versions
  // widget.js's own asset URL (?v=...) whenever its content changes,
  // but widget.css is loaded via a hand-built URL below with no such
  // versioning — without a cache-busting query string here, browsers
  // and Shopify's CDN can keep serving a stale cached widget.css
  // indefinitely after an update.
  var WIDGET_CSS_VERSION = '16';

  var CONFIG = window.CHAT_CONFIG;
  if (!CONFIG || !CONFIG.SUPABASE_URL || !CONFIG.SUPABASE_ANON_KEY) {
    console.error('[chat-widget] window.CHAT_CONFIG not found. Check snippet.liquid.');
    return;
  }

  // Path to this script, used to find widget.css next to widget.js
  var currentScript = document.currentScript;
  var scriptBase = currentScript
    ? currentScript.src.replace(/widget\.js(\?.*)?$/, '')
    : '';

  loadCss(scriptBase + 'widget.css?v=' + WIDGET_CSS_VERSION);
  loadScript('https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2/dist/umd/supabase.js', init);

  function loadCss(href) {
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  function loadScript(src, onload) {
    var script = document.createElement('script');
    script.src = src;
    script.onload = onload;
    script.onerror = function () {
      console.error('[chat-widget] failed to load supabase-js');
    };
    document.head.appendChild(script);
  }

  function init() {
    var supabase = window.supabase.createClient(CONFIG.SUPABASE_URL, CONFIG.SUPABASE_ANON_KEY);

    var visitorId = getOrCreateVisitorId();
    var conversationId = localStorage.getItem('sc_conversation_id');

    var els = buildWidgetDom();
    document.body.appendChild(els.root);

    var isOpen = false;
    var messagesLoaded = false;
    var renderedMessageIds = {};
    var messagesById = {};
    var replyingTo = null;
    var pollTimer = null;
    var audioCtx = null;
    var fileErrorTimer = null;
    var SWIPE_THRESHOLD = 50;
    var SWIPE_MAX = 70;
    var LAST_READ_MARKER_KEY = 'sc_last_read_marker';

    // Browsers only allow starting audio after a real user gesture.
    // Unlock it on the first click anywhere on the page, so it's
    // already running by the time a poll needs to play a sound.
    document.addEventListener('click', function unlockAudio() {
      document.removeEventListener('click', unlockAudio);
      try {
        if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        if (audioCtx.state === 'suspended') audioCtx.resume();
      } catch (e) { /* ignore */ }
    });

    els.toggleBtn.addEventListener('click', function () {
      isOpen = !isOpen;
      els.window.classList.toggle('sc-open', isOpen);
      if (isOpen) {
        if (!messagesLoaded) loadHistory();
        clearUnreadBadge();
        startPolling();
      } else {
        closeEmojiPanel();
      }
    });

    els.closeBtn.addEventListener('click', function () {
      isOpen = false;
      els.window.classList.remove('sc-open');
      closeEmojiPanel();
    });

    els.sendBtn.addEventListener('click', function () { sendMessage(); });
    els.input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    });
    els.input.addEventListener('input', function () {
      autoGrowInput();
    });
    autoGrowInput();

    els.attachBtn.addEventListener('click', function () {
      els.fileInput.click();
    });
    els.fileInput.addEventListener('change', function () {
      var files = Array.prototype.slice.call(els.fileInput.files || []);
      if (files.length) handleFilesSelected(files);
    });

    els.emojiBtn.addEventListener('click', function () {
      els.emojiPanel.classList.toggle('sc-open');
    });
    els.emojiPanel.addEventListener('click', function (e) {
      var emoji = e.target.getAttribute('data-emoji');
      if (emoji) insertEmoji(emoji);
    });

    els.replyCancelBtn.addEventListener('click', clearReplyTarget);

    // If a conversation already exists, refresh the current page/product
    // context on every load (step 5). Load the existing history first
    // (silently, without opening the window) so renderedMessageIds has a
    // baseline before polling starts — otherwise, since this is a fresh
    // page load, every already-read message looks "new" to the poller
    // and re-triggers the sound/badge for replies the customer already
    // saw before navigating. Only flag the badge here if a seller reply
    // is genuinely unseen (after the persisted last-read marker).
    if (conversationId) {
      updateConversationContext(conversationId);
      loadHistory().then(function () {
        showUnreadBadgeIfNeeded();
        startPolling();
      });
    }

    function closeEmojiPanel() {
      els.emojiPanel.classList.remove('sc-open');
    }

    function insertEmoji(emoji) {
      var start = els.input.selectionStart != null ? els.input.selectionStart : els.input.value.length;
      var end = els.input.selectionEnd != null ? els.input.selectionEnd : els.input.value.length;
      var val = els.input.value;
      els.input.value = val.slice(0, start) + emoji + val.slice(end);
      var newPos = start + emoji.length;
      els.input.focus();
      els.input.setSelectionRange(newPos, newPos);
      closeEmojiPanel();
      autoGrowInput();
    }

    // Grows the textarea to fit its content up to MAX_INPUT_LINES, then
    // switches to the browser's normal scrollbar beyond that — instead
    // of staying pinned at one line height and scrolling internally
    // from the very first wrapped line, which hides most of what's typed.
    function autoGrowInput() {
      var el = els.input;
      var style = getComputedStyle(el);
      var lineHeight = parseFloat(style.lineHeight);
      var maxHeight = lineHeight * MAX_INPUT_LINES
        + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
        + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);

      el.style.height = 'auto';
      var newHeight = Math.min(el.scrollHeight, maxHeight);
      el.style.height = newHeight + 'px';
      el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }

    function getOrCreateVisitorId() {
      var id = localStorage.getItem('sc_visitor_id');
      if (!id) {
        id = (crypto.randomUUID ? crypto.randomUUID() : 'v-' + Date.now() + '-' + Math.random().toString(16).slice(2));
        localStorage.setItem('sc_visitor_id', id);
      }
      return id;
    }

    function getProductInfo() {
      var info = window.chatProductInfo || {};
      var customerInfo = window.chatCustomerInfo || {};
      return {
        current_url: window.location.href,
        current_product_title: info.title || null,
        current_product_price: info.price || null,
        shopify_customer_name: customerInfo.name || null,
      };
    }

    function updateConversationContext(convId) {
      var info = getProductInfo();
      supabase
        .rpc('update_conversation_context', {
          p_conversation_id: convId,
          p_visitor_id: visitorId,
          p_current_url: info.current_url,
          p_product_title: info.current_product_title,
          p_product_price: info.current_product_price,
          p_shopify_customer_name: info.shopify_customer_name,
        })
        .then(function (res) {
          if (res.error) console.error('[chat-widget] failed to update context', res.error);
        });
    }

    function loadHistory() {
      messagesLoaded = true;
      if (!conversationId) {
        renderEmptyState();
        return Promise.resolve();
      }
      return fetchMessages().then(function (messages) {
        if (!messages) return;
        els.messages.innerHTML = '';
        renderedMessageIds = {};
        messagesById = {};
        if (messages.length === 0) {
          renderEmptyState();
        } else {
          messages.forEach(function (m) {
            renderedMessageIds[m.id] = true;
            renderMessage(m);
          });
          scrollToBottom();
        }
      });
    }

    function fetchMessages() {
      return supabase
        .rpc('get_conversation_messages', { p_conversation_id: conversationId, p_visitor_id: visitorId })
        .then(function (res) {
          if (res.error) {
            console.error('[chat-widget] failed to load history', res.error);
            return null;
          }
          return res.data || [];
        });
    }

    function startPolling() {
      if (pollTimer || !conversationId) return;
      pollTimer = setInterval(pollForNewMessages, POLL_INTERVAL_MS);
    }

    function stopPolling() {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    }

    function pollForNewMessages() {
      fetchMessages().then(function (messages) {
        if (!messages) return;
        var hasNew = false;
        var hasNewSeller = false;
        messages.forEach(function (m) {
          if (!renderedMessageIds[m.id]) {
            renderedMessageIds[m.id] = true;
            renderMessage(m);
            hasNew = true;
            if (m.sender === 'seller') hasNewSeller = true;
          }
        });
        if (hasNewSeller) {
          playNotificationSound();
          if (isOpen) {
            // Being shown live in the open window counts as read.
            setLastReadMarker();
          } else {
            showUnreadBadge();
          }
        }
        if (hasNew && isOpen) scrollToBottom();
      });
    }

    // Shows the badge on page load only if a seller reply exists that
    // the customer hasn't actually seen yet (i.e. sent after the last
    // time they opened the chat, persisted across page navigations via
    // localStorage). Called once after the silent history preload, and
    // deliberately does not play a sound — this is restoring known
    // state, not a live arrival.
    function showUnreadBadgeIfNeeded() {
      if (isOpen) return;
      var lastReadAt = getLastReadMarker();
      var hasUnreadSeller = Object.keys(messagesById).some(function (id) {
        var m = messagesById[id];
        return m.sender === 'seller' && (!lastReadAt || new Date(m.created_at) > new Date(lastReadAt));
      });
      if (hasUnreadSeller) showUnreadBadge();
    }

    function showUnreadBadge() {
      els.unreadBadge.style.display = 'block';
    }

    function clearUnreadBadge() {
      els.unreadBadge.style.display = 'none';
      setLastReadMarker();
    }

    function getLastReadMarker() {
      try {
        var raw = localStorage.getItem(LAST_READ_MARKER_KEY);
        if (!raw) return null;
        var parsed = JSON.parse(raw);
        return parsed.conversationId === conversationId ? parsed.at : null;
      } catch (e) {
        return null;
      }
    }

    function setLastReadMarker() {
      try {
        localStorage.setItem(LAST_READ_MARKER_KEY, JSON.stringify({ conversationId: conversationId, at: new Date().toISOString() }));
      } catch (e) { /* ignore */ }
    }

    function playNotificationSound() {
      try {
        if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        if (audioCtx.state === 'suspended') audioCtx.resume();
        var osc = audioCtx.createOscillator();
        var gain = audioCtx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.15, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.3);
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.start();
        osc.stop(audioCtx.currentTime + 0.3);
      } catch (e) {
        // Autoplay restrictions or no Web Audio support — not critical.
      }
    }

    function renderEmptyState() {
      els.messages.innerHTML = '<div class="sc-empty-state">Send us a message, we\'ll reply as soon as possible!</div>';
    }

    function renderMessage(msg) {
      messagesById[msg.id] = msg;

      var empty = els.messages.querySelector('.sc-empty-state');
      if (empty) empty.remove();

      var wrap = document.createElement('div');
      wrap.className = 'sc-msg-wrap ' + (msg.sender === 'customer' ? 'sc-msg-wrap-customer' : 'sc-msg-wrap-seller');

      var time = document.createElement('div');
      time.className = 'sc-msg-time';
      time.textContent = formatMessageTime(msg.created_at);
      wrap.appendChild(time);

      var bubble = document.createElement('div');
      bubble.className = 'sc-msg ' + (msg.sender === 'customer' ? 'sc-msg-customer' : 'sc-msg-seller');
      bubble.setAttribute('data-message-id', msg.id);

      if (msg.reply_to_message_id) {
        var quoted = messagesById[msg.reply_to_message_id];
        if (quoted) {
          bubble.appendChild(buildQuoteEl(quoted));
        }
      }

      if (msg.image_url) {
        var img = document.createElement('img');
        img.className = 'sc-msg-img';
        img.src = msg.image_url;
        img.loading = 'lazy';
        img.addEventListener('click', function () {
          window.open(msg.image_url, '_blank', 'noopener');
        });
        bubble.appendChild(img);
      }

      if (msg.body) {
        var text = document.createElement('div');
        text.className = 'sc-msg-text';
        text.textContent = msg.body;
        bubble.appendChild(text);
      }

      // Swipe (touch) covers phones; mouse users get a hover-reveal
      // reply button instead, since swiping isn't a thing without a
      // touchscreen.
      var replyBtn = document.createElement('button');
      replyBtn.type = 'button';
      replyBtn.className = 'sc-msg-reply-btn';
      replyBtn.innerHTML = '&#8617;';
      replyBtn.setAttribute('aria-label', 'Reply to this message');
      replyBtn.addEventListener('click', function () {
        setReplyTarget(msg);
      });
      bubble.appendChild(replyBtn);

      attachSwipeToReply(bubble, msg);
      wrap.appendChild(bubble);
      els.messages.appendChild(wrap);
      updateHeaderTitle();
    }

    // "Have a question?" is only useful before the conversation has
    // actually started — once there's at least one message, it just
    // sits there stating the obvious above an ongoing chat.
    function updateHeaderTitle() {
      if (!els.headerTitle) return;
      els.headerTitle.style.display = Object.keys(messagesById).length > 0 ? 'none' : '';
    }

    function formatMessageTime(iso) {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      var h = d.getHours();
      var m = d.getMinutes();
      return (h < 10 ? '0' : '') + h + ':' + (m < 10 ? '0' : '') + m;
    }

    function quotePreviewText(msg) {
      if (msg.body) return msg.body.length > 80 ? msg.body.slice(0, 80) + '…' : msg.body;
      if (msg.image_url) return '📷 Photo';
      return '';
    }

    // Builds the little quoted-message block shown inside a reply,
    // with a thumbnail if the original had a photo. Clicking it jumps
    // to (and briefly highlights) the original message.
    function buildQuoteEl(quoted) {
      var quoteEl = document.createElement('div');
      quoteEl.className = 'sc-msg-quote';

      if (quoted.image_url) {
        var thumb = document.createElement('img');
        thumb.className = 'sc-msg-quote-img';
        thumb.src = quoted.image_url;
        thumb.loading = 'lazy';
        quoteEl.appendChild(thumb);
      }
      if (quoted.body) {
        var quoteText = document.createElement('div');
        quoteText.textContent = quotePreviewText(quoted);
        quoteEl.appendChild(quoteText);
      }

      quoteEl.addEventListener('click', function (e) {
        e.stopPropagation();
        jumpToMessage(quoted.id);
      });

      return quoteEl;
    }

    function jumpToMessage(id) {
      var target = els.messages.querySelector('[data-message-id="' + id + '"]');
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('sc-msg-highlight');
      setTimeout(function () {
        target.classList.remove('sc-msg-highlight');
      }, 1500);
    }

    function setReplyTarget(msg) {
      replyingTo = msg;
      els.replyPreview.classList.add('sc-open');
      els.replyPreviewText.textContent = quotePreviewText(msg);
      if (msg.image_url) {
        els.replyPreviewImg.src = msg.image_url;
        els.replyPreviewImg.style.display = 'block';
      } else {
        els.replyPreviewImg.style.display = 'none';
        els.replyPreviewImg.removeAttribute('src');
      }
      els.input.focus();
    }

    function clearReplyTarget() {
      replyingTo = null;
      els.replyPreview.classList.remove('sc-open');
      els.replyPreviewImg.style.display = 'none';
      els.replyPreviewImg.removeAttribute('src');
    }

    function attachSwipeToReply(bubbleEl, msg) {
      var startX = 0;
      var startY = 0;
      var deltaX = 0;
      var dragging = false;
      var decided = false;
      var horizontal = false;

      bubbleEl.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        deltaX = 0;
        dragging = true;
        decided = false;
        horizontal = false;
        bubbleEl.style.transition = 'none';
      }, { passive: true });

      bubbleEl.addEventListener('touchmove', function (e) {
        if (!dragging) return;
        var dx = e.touches[0].clientX - startX;
        var dy = e.touches[0].clientY - startY;

        if (!decided) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          decided = true;
          horizontal = Math.abs(dx) > Math.abs(dy);
        }
        if (!horizontal || dx <= 0) return;

        deltaX = Math.min(dx, SWIPE_MAX);
        bubbleEl.style.transform = 'translateX(' + deltaX + 'px)';
        bubbleEl.classList.toggle('sc-swipe-armed', deltaX >= SWIPE_THRESHOLD);
        e.preventDefault();
      }, { passive: false });

      function endSwipe() {
        if (!dragging) return;
        dragging = false;
        bubbleEl.style.transition = 'transform 0.2s ease';
        bubbleEl.style.transform = 'translateX(0)';
        bubbleEl.classList.remove('sc-swipe-armed');
        if (horizontal && deltaX >= SWIPE_THRESHOLD) setReplyTarget(msg);
        deltaX = 0;
      }

      bubbleEl.addEventListener('touchend', endSwipe);
      bubbleEl.addEventListener('touchcancel', endSwipe);
    }

    function scrollToBottom() {
      els.messages.scrollTop = els.messages.scrollHeight;
    }

    function sendMessage(imageUrl, isRetry) {
      var body = els.input.value.trim();
      if (!body && !imageUrl) return;

      var replyToId = replyingTo ? replyingTo.id : null;
      els.sendBtn.disabled = true;

      return ensureConversation()
        .then(function (convId) {
          return supabase.rpc('send_customer_message', {
            p_conversation_id: convId,
            p_visitor_id: visitorId,
            p_body: body,
            p_image_url: imageUrl || null,
            p_reply_to_message_id: replyToId,
          });
        })
        .then(function (res) {
          if (res.error) throw res.error;
          messagesLoaded = true;
          renderedMessageIds[res.data.id] = true;
          renderMessage(res.data);
          scrollToBottom();
          els.input.value = '';
          autoGrowInput();
          clearReplyTarget();
          startPolling();
        })
        .catch(function (err) {
          console.error('[chat-widget] failed to send message', err);
          // The conversation this browser remembers no longer exists
          // (e.g. deleted from the seller dashboard) — start a fresh
          // one instead of getting stuck retrying the same dead id
          // forever.
          if (!isRetry && err && err.message === 'Conversation not found') {
            resetConversationState();
            sendMessage(imageUrl, true);
          }
        })
        .finally(function () {
          els.sendBtn.disabled = false;
        });
    }

    function resetConversationState() {
      conversationId = null;
      localStorage.removeItem('sc_conversation_id');
      renderedMessageIds = {};
      messagesById = {};
      messagesLoaded = false;
      stopPolling();
      updateHeaderTitle();
    }

    function showFileError(message) {
      els.fileError.textContent = message;
      els.fileError.classList.add('sc-open');
      if (fileErrorTimer) clearTimeout(fileErrorTimer);
      fileErrorTimer = setTimeout(function () {
        els.fileError.classList.remove('sc-open');
      }, 4000);
    }

    // Multiple photos can be picked at once, but they're sent as separate
    // messages one at a time (not in parallel) — same pipeline as a single
    // photo, just queued, so message order stays predictable and the
    // connection isn't hit with a burst of simultaneous uploads.
    function handleFilesSelected(files) {
      els.attachBtn.disabled = true;
      els.sendBtn.disabled = true;

      var chain = Promise.resolve();
      files.forEach(function (file) {
        chain = chain.then(function () { return sendOnePhoto(file); });
      });
      chain.finally(function () {
        els.attachBtn.disabled = false;
        els.sendBtn.disabled = false;
        els.fileInput.value = '';
      });
    }

    function sendOnePhoto(file) {
      if (ALLOWED_IMAGE_TYPES.indexOf(file.type) === -1) {
        showFileError('Only JPG, PNG, HEIC, and WebP images are supported.');
        return Promise.resolve();
      }
      if (file.size > MAX_IMAGE_BYTES) {
        showFileError('Image is too large (max 5MB).');
        return Promise.resolve();
      }

      return compressImage(file)
        .then(function (finalFile) {
          return uploadImage(finalFile);
        })
        .then(function (url) {
          return sendMessage(url);
        })
        .catch(function (err) {
          console.error('[chat-widget] failed to upload image', err);
        });
    }

    // Resizes/re-encodes an image to roughly TARGET_IMAGE_BYTES as a
    // JPEG. Skips files already under the target and already in a
    // universally-viewable format — no point degrading an already-small
    // JPEG/PNG. HEIC/WebP always go through conversion regardless of
    // size (see TYPES_REQUIRING_CONVERSION above). Falls back to the
    // original file if compression fails for any reason (canvas errors,
    // browser can't decode this format, etc.) rather than blocking the
    // whole send — the Storage bucket accepts these formats too, as a
    // safety net for exactly this case.
    function compressImage(file) {
      var needsConversion = TYPES_REQUIRING_CONVERSION.indexOf(file.type) !== -1;
      if (!needsConversion && file.size <= TARGET_IMAGE_BYTES) return Promise.resolve(file);

      return loadImageSource(file)
        .then(function (source) {
          var width = source.width || source.naturalWidth;
          var height = source.height || source.naturalHeight;
          var scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(width, height));
          width = Math.max(1, Math.round(width * scale));
          height = Math.max(1, Math.round(height * scale));

          var canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          var ctx = canvas.getContext('2d');
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(source, 0, 0, width, height);

          return canvasToCompressedBlob(canvas);
        })
        .then(function (blob) {
          var baseName = file.name.replace(/\.[a-zA-Z0-9]+$/, '') || 'photo';
          return new File([blob], baseName + '.jpg', { type: 'image/jpeg' });
        })
        .catch(function (err) {
          console.error('[chat-widget] image compression failed, using original', err);
          return file;
        });
    }

    function loadImageSource(file) {
      if (window.createImageBitmap) {
        return createImageBitmap(file);
      }
      return new Promise(function (resolve, reject) {
        var img = new Image();
        var url = URL.createObjectURL(file);
        img.onload = function () {
          URL.revokeObjectURL(url);
          resolve(img);
        };
        img.onerror = function (e) {
          URL.revokeObjectURL(url);
          reject(e);
        };
        img.src = url;
      });
    }

    function canvasToCompressedBlob(canvas) {
      var qualities = [0.8, 0.6, 0.4, 0.25, 0.15];
      var i = 0;

      function attempt() {
        return new Promise(function (resolve, reject) {
          canvas.toBlob(function (blob) {
            if (!blob) { reject(new Error('canvas.toBlob returned null')); return; }
            resolve(blob);
          }, 'image/jpeg', qualities[i]);
        }).then(function (blob) {
          if (blob.size <= TARGET_IMAGE_BYTES || i === qualities.length - 1) return blob;
          i++;
          return attempt();
        });
      }

      return attempt();
    }

    function uploadImage(file) {
      var ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      var path = visitorId + '/' + (crypto.randomUUID ? crypto.randomUUID() : Date.now()) + '.' + ext;

      return supabase.storage
        .from('chat-attachments')
        .upload(path, file, { contentType: file.type, upsert: false })
        .then(function (res) {
          if (res.error) throw res.error;
          var pub = supabase.storage.from('chat-attachments').getPublicUrl(path);
          return pub.data.publicUrl;
        });
    }

    function ensureConversation() {
      if (conversationId) return Promise.resolve(conversationId);

      var info = getProductInfo();

      return supabase
        .rpc('create_conversation', {
          p_visitor_id: visitorId,
          p_current_url: info.current_url,
          p_product_title: info.current_product_title,
          p_product_price: info.current_product_price,
          p_shopify_customer_name: info.shopify_customer_name,
        })
        .then(function (res) {
          if (res.error) throw res.error;
          conversationId = res.data.id;
          localStorage.setItem('sc_conversation_id', conversationId);
          return conversationId;
        });
    }

    function buildWidgetDom() {
      var root = document.createElement('div');
      root.id = 'sc-widget-root';

      var toggleBtn = document.createElement('button');
      toggleBtn.className = 'sc-toggle-btn';
      toggleBtn.setAttribute('aria-label', 'Open chat');
      toggleBtn.innerHTML = '&#128172;';

      var unreadBadge = document.createElement('span');
      unreadBadge.className = 'sc-unread-badge';
      toggleBtn.appendChild(unreadBadge);

      var win = document.createElement('div');
      win.className = 'sc-window';

      var header = document.createElement('div');
      header.className = 'sc-header';
      var headerTitle = document.createElement('span');
      headerTitle.textContent = 'Have a question?';
      header.appendChild(headerTitle);

      var closeBtn = document.createElement('button');
      closeBtn.className = 'sc-header-close';
      closeBtn.innerHTML = '&times;';
      closeBtn.setAttribute('aria-label', 'Close chat');
      header.appendChild(closeBtn);

      var messages = document.createElement('div');
      messages.className = 'sc-messages';

      var emojiPanel = document.createElement('div');
      emojiPanel.className = 'sc-emoji-panel';
      EMOJIS.forEach(function (emoji) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sc-emoji-item';
        btn.setAttribute('data-emoji', emoji);
        btn.textContent = emoji;
        emojiPanel.appendChild(btn);
      });

      var replyPreview = document.createElement('div');
      replyPreview.className = 'sc-reply-preview';
      var replyPreviewImg = document.createElement('img');
      replyPreviewImg.className = 'sc-reply-preview-img';
      replyPreviewImg.style.display = 'none';
      var replyPreviewText = document.createElement('div');
      replyPreviewText.className = 'sc-reply-preview-text';
      var replyCancelBtn = document.createElement('button');
      replyCancelBtn.type = 'button';
      replyCancelBtn.className = 'sc-reply-cancel';
      replyCancelBtn.innerHTML = '&times;';
      replyCancelBtn.setAttribute('aria-label', 'Cancel reply');
      replyPreview.appendChild(replyPreviewImg);
      replyPreview.appendChild(replyPreviewText);
      replyPreview.appendChild(replyCancelBtn);

      var fileError = document.createElement('div');
      fileError.className = 'sc-file-error';

      var inputRow = document.createElement('div');
      inputRow.className = 'sc-input-row';

      var attachBtn = document.createElement('button');
      attachBtn.type = 'button';
      attachBtn.className = 'sc-icon-btn';
      attachBtn.setAttribute('aria-label', 'Attach photo');
      attachBtn.innerHTML = '<span class="sc-icon-emoji">&#128206;</span>' + ATTACH_ICON_SVG;

      var fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/jpeg,image/png,image/heic,image/heif,image/webp';
      fileInput.multiple = true;
      fileInput.className = 'sc-file-input';

      var emojiBtn = document.createElement('button');
      emojiBtn.type = 'button';
      emojiBtn.className = 'sc-icon-btn';
      emojiBtn.setAttribute('aria-label', 'Insert emoji');
      emojiBtn.innerHTML = '<span class="sc-icon-emoji">&#128512;</span>' + EMOJI_ICON_SVG;

      var input = document.createElement('textarea');
      input.className = 'sc-input';
      input.rows = 1;
      input.placeholder = 'Type a message...';

      var sendBtn = document.createElement('button');
      sendBtn.className = 'sc-send-btn';
      sendBtn.textContent = 'Send';

      inputRow.appendChild(attachBtn);
      inputRow.appendChild(fileInput);
      inputRow.appendChild(emojiBtn);
      inputRow.appendChild(input);
      inputRow.appendChild(sendBtn);
      inputRow.appendChild(emojiPanel);

      win.appendChild(header);
      win.appendChild(messages);
      win.appendChild(replyPreview);
      win.appendChild(fileError);
      win.appendChild(inputRow);

      root.appendChild(win);
      root.appendChild(toggleBtn);

      return {
        root: root,
        toggleBtn: toggleBtn,
        unreadBadge: unreadBadge,
        window: win,
        closeBtn: closeBtn,
        headerTitle: headerTitle,
        messages: messages,
        emojiPanel: emojiPanel,
        attachBtn: attachBtn,
        fileInput: fileInput,
        emojiBtn: emojiBtn,
        input: input,
        sendBtn: sendBtn,
        replyPreview: replyPreview,
        replyPreviewImg: replyPreviewImg,
        replyPreviewText: replyPreviewText,
        replyCancelBtn: replyCancelBtn,
        fileError: fileError,
      };
    }
  }
})();
